import { Avatar } from "@/components/kit";
import { sidePeople } from "@/components/OwnershipEditor";
import { firstName, fromUnits, inr, toUnits } from "@/lib/format";

const S = 100;

export function shareByOwnership(total, alloc) {
  const T = toUnits(total, S);
  const entries = Object.entries(alloc).filter(([, v]) => Number(v) > 0);
  const out = {};
  let used = 0;
  entries.forEach(([pid, v], i) => {
    const share = i === entries.length - 1 ? T - used : Math.round((T * toUnits(v, 10000)) / 1000000);
    used += share;
    out[pid] = fromUnits(share, S, 2);
  });
  return out;
}

function Preview({ kartas, members, total, alloc, value, receipt }) {
  const side = Object.fromEntries(members.map((m) => [m.person_id, m.side_karta_id]));
  const exp = shareByOwnership(total, alloc);
  const rows = kartas.map((k) => {
    const e = Object.entries(exp).filter(([p]) => side[p] === k.id).reduce((a, [, v]) => a + toUnits(v, S), 0);
    const a = Object.entries(value).filter(([p]) => side[p] === k.id).reduce((x, [, v]) => x + toUnits(v, S), 0);
    return { k, e, a, net: receipt ? e - a : a - e };
  });
  const creditor = rows.find((r) => r.net > 0);
  const debtor = rows.find((r) => r.net < 0);
  return (
    <div className="hd-card bg-[#FBF8F4]" data-testid="funding-preview">
      <div className="hd-label mb-2">{receipt ? "Entitled vs received" : "Expected vs actual contribution"}</div>
      {rows.map((r) => (
        <div key={r.k.id} className="flex justify-between text-sm py-1">
          <span className="font-semibold">{firstName(r.k.full_name)} Side</span>
          <span className="tabular-nums text-[#4A5568]">{inr(fromUnits(r.e, S, 2))} · {receipt ? "got" : "paid"} {inr(fromUnits(r.a, S, 2))}</span>
        </div>
      ))}
      <div className="mt-2 text-sm font-bold text-[#B34832]" data-testid="funding-preview-result">
        {creditor && debtor
          ? `${firstName(debtor.k.full_name)} Side will owe ${firstName(creditor.k.full_name)} Side ${inr(fromUnits(creditor.net, S, 2))}`
          : "Balanced — nobody owes anything for this entry"}
      </div>
    </div>
  );
}

export default function FundingEditor({ members, kartas, total, value, onChange, alloc = {}, receipt = false }) {
  const T = toUnits(total, S);
  const entered = Object.values(value).reduce((a, v) => a + toUnits(v, S), 0);
  const sides = sidePeople(members, kartas, value);
  const ok = entered === T;
  return (
    <div className="space-y-4" data-testid="funding-editor">
      <div className="hd-card flex items-center justify-between">
        <div>
          <div className="hd-label">{receipt ? "Total proceeds" : "Total to fund"}</div>
          <div className="text-2xl font-bold tabular-nums mt-0.5" data-testid="funding-total">{inr(total)}</div>
        </div>
      </div>
      <div className="flex gap-2 flex-wrap">
        {kartas.map((k) => (
          <button type="button" key={k.id} data-testid={`fund-all-${k.id}`} onClick={() => onChange({ [k.id]: total })}
            className="hd-chip bg-white border border-[#E2D9CF] px-3.5 py-2 text-sm hover:border-[#C8553D] transition-colors">
            {firstName(k.full_name)} {receipt ? "received" : "paid"} all
          </button>
        ))}
        <button type="button" data-testid="fund-as-ownership" onClick={() => onChange(shareByOwnership(total, alloc))}
          className="hd-chip bg-white border border-[#E2D9CF] px-3.5 py-2 text-sm hover:border-[#C8553D] transition-colors">
          As per ownership
        </button>
      </div>
      {sides.map((s) => (
        <div key={s.k.id} className="hd-card">
          <div className="font-bold mb-1">{firstName(s.k.full_name)} Side</div>
          {s.people.map((p) => (
            <div key={p.person_id} className="flex items-center gap-3 py-2">
              <Avatar name={p.full_name} id={p.person_id} size={34} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{p.full_name}</div>
                <div className="text-[11px] text-[#718096] hs-id">{p.hs_id}</div>
              </div>
              <div className="relative w-36">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#718096]">₹</span>
                <input data-testid={`fund-input-${p.person_id}`} className="hd-input h-11 pl-7 text-right" inputMode="decimal" placeholder="0"
                  value={value[p.person_id] ?? ""} onChange={(e) => onChange({ ...value, [p.person_id]: e.target.value.replace(/[^0-9.]/g, "") })} />
              </div>
            </div>
          ))}
        </div>
      ))}
      <div data-testid="funding-status" className={`rounded-xl px-4 py-3 text-sm font-semibold ${ok ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"}`}>
        {ok ? `Matches ${inr(total)}` : `Entered ${inr(fromUnits(entered, S, 2))} — ${entered < T ? `${inr(fromUnits(T - entered, S, 2))} remaining` : `${inr(fromUnits(entered - T, S, 2))} too much`}`}
      </div>
      {Object.keys(alloc).length > 0 && <Preview kartas={kartas} members={members} total={total} alloc={alloc} value={value} receipt={receipt} />}
    </div>
  );
}
